import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './config';

/**
 * FIREBASE COLLECTION ARCHITECTURE
 * businesses/{businessId} -> Temel işletme bilgileri
 * businesses/{businessId}/settings/{settingType} -> Gelişmiş ayarlar (subcollection)
 * businesses/{businessId}/tables/{tableId} -> Masa yönetimi (subcollection)
 */

/**
 * Firebase Business Service
 * İşletme bilgilerinin Firebase'de yönetimi
 */

export class FirebaseBusinessService {

  // Check if Firebase is available
  static isFirebaseAvailable() {
    return db !== null;
  }

  // 🏢 GET BUSINESS INFO FROM HIBRIT STRUCTURE
  static async getBusinessById(businessId) {
    try {
      // Mock data if Firebase not available
      if (!this.isFirebaseAvailable()) {
        console.warn('📝 Firebase not available, returning mock data');
        return this.getBusinessDataTemplate();
      }

      console.log('🔄 Loading business from hibrit structure:', businessId);

      // Helper to normalize raw Firestore data
      const normalize = (rawData, id) => ({
        id,
        name: rawData.name || '',
        description: rawData.description || '',
        businessType: rawData.businessType || rawData.business_type || rawData.subcategory || 'restaurant',
        logo: rawData.logo || '',
        logoPath: rawData.logoPath || '',
        phone: rawData.phone || '',
        email: rawData.email || '',
        website: rawData.website || '',
        address: typeof rawData.address === 'string'
          ? { street: rawData.address, city: '', state: '', zipCode: '', country: 'TR' }
          : (rawData.address || { street: '', city: '', state: '', zipCode: '', country: 'TR' }),
        taxNumber: rawData.taxNumber || rawData.tax_id || '',
        commercialTitle: rawData.commercialTitle || rawData.commercial_title || '',
        sector: rawData.sector || rawData.business_type || 'food_beverage',
        status: rawData.status || 'active',
        ownerId: rawData.ownerId || rawData.owner_id || rawData.owner_account_id || '',
        ownerName: rawData.ownerName || rawData.owner_name || '',
        settings: {
          currency: rawData.settings?.currency || 'TRY',
          timezone: rawData.settings?.timezone || 'Europe/Istanbul',
          language: rawData.settings?.language || 'tr',
          ...rawData.settings
        },
        createdAt: rawData.createdAt || rawData.created_at,
        updatedAt: rawData.updatedAt || rawData.updated_at
      });

      // --- Path 1: businesses/{sector}/businesses/{businessId} (YENİ HİBRİT YAPI) ---
      const sectors = ['food_beverage', 'service', 'retail', 'hospitality'];
      for (const sector of sectors) {
        try {
          const nestedRef = doc(db, 'businesses', sector, 'businesses', businessId);
          const nestedSnap = await getDoc(nestedRef);
          if (nestedSnap.exists()) {
            const data = normalize(nestedSnap.data(), businessId);
            console.log(`✅ Business info loaded from nested path (${sector}):`, data);
            return data;
          }
        } catch (nestErr) {
          // Silent - continue to next sector
        }
      }

      // --- Path 2: businesses/{businessId} (ESKİ/düz yapı) ---
      const businessInfoRef = doc(db, 'businesses', businessId);
      const businessInfoSnap = await getDoc(businessInfoRef);
      if (businessInfoSnap.exists()) {
        const data = normalize(businessInfoSnap.data(), businessId);
        console.log('✅ Business info loaded from flat hibrit structure:', data);
        return data;
      }

      // --- Path 3: restaurants/{businessId} (legacy fallback) ---
      try {
        const legacyRef = doc(db, 'restaurants', businessId);
        const legacySnap = await getDoc(legacyRef);
        if (legacySnap.exists()) {
          const data = normalize(legacySnap.data(), businessId);
          console.log('📦 Found legacy restaurant data:', data);
          return data;
        }
      } catch (legacyErr) {
        console.warn('⚠️ Legacy restaurants lookup failed:', legacyErr.message);
      }

      console.log('❌ Business info not found in any known path for businessId:', businessId);
      return null;

    } catch (error) {
      console.error('❌ Error getting business from hibrit structure:', error);
      throw error;
    }
  }


  // 🏢 CREATE OR UPDATE BUSINESS
  static async saveBusinessInfo(businessId, businessData) {
    try {
      // Mock save if Firebase not available
      if (!this.isFirebaseAvailable()) {
        console.warn('📝 Firebase not available, mocking save operation');
        return { ...businessData, updatedAt: new Date() };
      }

      console.log('💾 Saving business info to hibrit structure:', businessId, businessData);
      
      // businesses/{businessId} dokümantına kaydet
      const businessInfoRef = doc(db, 'businesses', businessId);
      
      // Timestamp ekle
      const timestamp = new Date();
      const hibritData = {
        ...businessData,
        updated_at: timestamp,
        business_id: businessId,
        // İlk kayıt ise created_at ekle
        ...(!businessData.created_at && { created_at: timestamp })
      };
      
      await setDoc(businessInfoRef, hibritData, { merge: true });
      
      console.log('✅ Business info saved to hibrit structure successfully:', businessId);
      return hibritData;
      
    } catch (error) {
      console.error('❌ Error saving business info:', error);
      throw error;
    }
  }

  // 🏢 UPDATE BUSINESS LOGO IN HIBRIT STRUCTURE
  static async updateBusinessLogo(businessId, logoUrl, logoPath = null) {
    try {
      // businesses/{businessId} dokümantını güncelle
      const businessInfoRef = doc(db, 'businesses', businessId);
      
      const updateData = {
        logo: logoUrl,
        updatedAt: new Date()
      };
      
      if (logoPath) {
        updateData.logoPath = logoPath; // Storage'daki path'i de kaydet
      }
      
      await updateDoc(businessInfoRef, updateData);
      
      console.log('✅ Business logo updated in hibrit structure:', businessId);
      return updateData;
      
    } catch (error) {
      console.error('❌ Error updating business logo in hibrit structure:', error);
      throw error;
    }
  }

  // 🔄 MIGRATE LEGACY DATA TO HIBRIT STRUCTURE
  static async migrateToHibritStructure(businessId, legacyData) {
    try {
      console.log('🔄 Migrating legacy business data to hibrit structure:', businessId);
      
      // Legacy veriden hibrit yapıya dönüştür
      const businessInfoData = {
        name: legacyData.name || '',
        description: legacyData.description || '',
        business_type: legacyData.businessType || legacyData.business_type || 'restaurant',
        sector: legacyData.sector || 'food_beverage',
        logo: legacyData.logo || '',
        logoPath: legacyData.logoPath || '',
        phone: legacyData.phone || '',
        email: legacyData.email || '',
        website: legacyData.website || '',
        address: legacyData.address?.street || legacyData.address || '',
        tax_id: legacyData.taxNumber || legacyData.tax_id || '',
        commercial_title: legacyData.commercialTitle || legacyData.commercial_title || '',
        settings: {
          currency: legacyData.settings?.currency || 'TRY',
          timezone: legacyData.settings?.timezone || 'Europe/Istanbul',
          language: legacyData.settings?.language || 'tr',
          date_format: legacyData.settings?.dateFormat || 'DD/MM/YYYY',
          time_format: legacyData.settings?.timeFormat || '24h',
          city: legacyData.address?.city || '',
          state: legacyData.address?.state || '',
          zipCode: legacyData.address?.zipCode || '',
          country: legacyData.address?.country || 'TR'
        },
        created_at: legacyData.createdAt || legacyData.created_at || new Date(),
        updated_at: new Date(),
        business_id: businessId,
        migration_info: {
          migrated_at: new Date(),
          migrated_from: 'legacy_businesses_collection',
          migration_version: '1.0'
        }
      };
      
      // businesses/{businessId}'ya kaydet
      const businessInfoRef = doc(db, 'businesses', businessId);
      await setDoc(businessInfoRef, businessInfoData);
      
      console.log('✅ Business data migrated to hibrit structure successfully');
      return businessInfoData;
      
    } catch (error) {
      console.error('❌ Error migrating to hibrit structure:', error);
      throw error;
    }
  }

  // 🏢 GET BUSINESSES BY OWNER
  static async getBusinessesByOwner(ownerId) {
    try {
      const businessesRef = collection(db, 'businesses');
      const q = query(businessesRef, where('ownerId', '==', ownerId));
      const querySnapshot = await getDocs(q);
      
      const businesses = [];
      querySnapshot.forEach((doc) => {
        businesses.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return businesses;
    } catch (error) {
      console.error('❌ Error getting businesses by owner:', error);
      throw error;
    }
  }

  // 🏢 UPDATE BUSINESS STATUS
  static async updateBusinessStatus(businessId, status) {
    try {
      const businessRef = doc(db, 'businesses', businessId);
      await updateDoc(businessRef, {
        status: status, // 'active', 'inactive', 'suspended'
        updatedAt: new Date()
      });
      
      console.log('✅ Business status updated:', businessId, status);
      
    } catch (error) {
      console.error('❌ Error updating business status:', error);
      throw error;
    }
  }

  // 🏢 BUSINESS DATA STRUCTURE EXAMPLE
  static getBusinessDataTemplate() {
    return {
      // Basic Info
      name: '',
      description: '',
      businessType: 'restaurant', // restaurant, retail, service
      logo: null,
      logoPath: null,
      
      // Contact Info
      phone: '',
      email: '',
      website: '',
      
      // Address
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'TR'
      },
      
      // Business Details
      taxNumber: '',
      commercialTitle: '',
      sector: '',
      
      // Operational
      status: 'active',
      
      // Owner Info
      ownerId: '',
      ownerName: '',
      
      // Settings
      settings: {
        currency: 'TRY',
        timezone: 'Europe/Istanbul',
        language: 'tr'
      },
      
      // Timestamps
      createdAt: null,
      updatedAt: null
    };
  }

  // 🏢 VALIDATE BUSINESS DATA
  static validateBusinessData(data) {
    const errors = [];
    
    if (!data.name || data.name.trim().length < 2) {
      errors.push('İşletme adı en az 2 karakter olmalıdır');
    }
    
    if (!data.businessType) {
      errors.push('İşletme türü seçilmelidir');
    }
    
    if (data.phone && !/^[+]?[\d\s-()]{10,}$/.test(data.phone)) {
      errors.push('Geçerli bir telefon numarası giriniz');
    }
    
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Geçerli bir e-posta adresi giriniz');
    }
    
    return errors;
  }
}

export default FirebaseBusinessService;
